import {
  Alert,
  Button,
  Col,
  ConfigProvider,
  Dropdown,
  Form,
  Menu,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  MoreOutlined,
  RetweetOutlined,
  RollbackOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import {
  COURIER_CHANGE_STATES,
  ORDER_STATUS_COLORS,
  ORDER_STATUSES,
  ORDER_TYPES,
  REPORT_TYPES,
} from '../../../../helpers/constants';
import {
  cancelOrderManually,
  deliverOrderManually,
  notDeliverOrderManually,
} from '../../../../store/requests/order';
import { useSelector } from 'react-redux';
import { getCourier } from '../../../../store/requests/courier';
import { getCollectionAndDeliveryDelayByReportType } from '../../../../store/requests/dashboard';
import moment from 'moment';

const OrderTransfer = lazy(
  () => import('../../../../components/Orders/OrderTransfer'),
);
const TableFilter = lazy(() => import('./TableFilter'));
const ChangeOrderCourier = lazy(
  () => import('../../../../components/Orders/ChangeOrderCourier'),
);
const Edit = lazy(() => import('../../../../components/Orders/Edit'));
const { confirm } = Modal;

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 30, 50],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};
const OrdersPendingAction = ({ route }) => {
  const history = useHistory();
  const {
    isViewEnabled,
    isCrudEnabled,
    changeCourier,
    changeStatus,
    edit,
    orderTransfer,
  } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [getCourierLoading, setGetCourierLoading] = useState(false);
  const [openOrderTransferModal, setOpenOrderTransferModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [couriersOfZone, setCouriersOfZone] = useState(null);
  const [filteringValue, setFilteringValue] = useState({
    reportType: REPORT_TYPES.DELAYED_COLLECTION_ON_TIME,
  });
  const [isChangeCourierModalOpen, setIsChangeCourierModalOpen] =
    useState(false);
  const [orderNumberForTransfer, setOrderNumberForTransfer] = useState(null);
  const [rows, setRows] = useState([]);
  const [tableParams, setTableParams] = useState(initialPageParams);
  const location = useLocation();
  const reportType = location?.state?.reportType;
  const activeStoreId = useSelector(
    (state) => state.auth?.user.activeStore?.id,
  );

  useEffect(() => {
    const newFilteringValue = reportType ? { reportType } : filteringValue;
    fetchData(tableParams, newFilteringValue);
  }, []);

  const clearHistoryState = () => {
    const updatedState = {
      ...history.location.state,
      reportType: null,
    };

    history.replace({
      ...history.location,
      state: updatedState,
    });
  };

  const fetchData = (tableParamsValue, filterValue) => {
    const hasLocationFilter =
      filterValue?.storeId ||
      filterValue?.regionId ||
      filterValue?.directorateId;
    if (filterValue?.reportType && (activeStoreId || hasLocationFilter)) {
      const requestConfig = getRequestConfig(tableParamsValue, filterValue);
      getCollectionAndDeliveryDelayByReportTypeReq(requestConfig);
    }
  };

  const getRequestConfig = (tableParamsValue, filterValue) => {
    let queryString = '';

    setFilteringValue(filterValue);

    queryString = `${queryString}?page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue?.column) {
      queryString += `&sort=${tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    const hasLocationFilter = filterValue.regionId || filterValue.directorateId;

    let locationBody = {};
    if (filterValue.storeId) {
      locationBody = { storeId: filterValue.storeId };
    } else if (filterValue.regionId) {
      locationBody = { regionId: filterValue.regionId };
    } else if (filterValue.directorateId) {
      locationBody = { directorateId: filterValue.directorateId };
    } else if (!hasLocationFilter && activeStoreId) {
      locationBody = { storeId: activeStoreId };
    }

    return {
      queryString,
      body: {
        reportType: filterValue.reportType,
        ...locationBody,
      },
    };
  };

  const getCollectionAndDeliveryDelayByReportTypeReq = (requestConfig) => {
    setLoading(true);
    getCollectionAndDeliveryDelayByReportType(requestConfig)
      .then((res) => {
        const { content, page } = res;
        setTableParams({
          pagination: {
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size,
          },
        });
        if (reportType) {
          clearHistoryState();
        }
        setRows(content);
      })
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      dataIndex: 'customerOrderNumber',
      title: t('orderId'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'partner',
      title: t('orderChannel'),
      sorter: true,
      showSorterTooltip: false,
      render: (partner, items) => {
        const orderChannel = {
          POSITIVE: 'ŞOK-Positive',
          TRANSFORMATION: 'ŞOK-Yeni Ecom',
        };
        return orderChannel[items.channel] ?? partner;
      },
    },
    {
      dataIndex: 'status',
      title: t('shipmentStatus'),
      sorter: true,
      showSorterTooltip: false,
      render: (status) => (
        <Tag color={ORDER_STATUS_COLORS[status]}>{t(status)}</Tag>
      ),
    },
    {
      dataIndex: 'slotStartTime',
      title: t('slotTime'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, record) =>
        `${record?.slotStartTime} - ${record?.slotEndTime}`,
    },
    {
      dataIndex: 'address',
      title: t('address'),
      sorter: true,
      showSorterTooltip: false,
      render: (address) => address ?? '-',
    },
    {
      dataIndex: 'createdDate',
      title: t('orderCreatedTime'),
      sorter: true,
      showSorterTooltip: false,
      render: (text, _) => moment(text).format('DD/MM/YYYY'),
    },
    {
      dataIndex: 'deliveryDate',
      title: t('deliveryDate'),
      sorter: true,
      showSorterTooltip: false,
      render: (text, _) => moment(text).format('DD/MM/YYYY'),
    },
    {
      dataIndex: 'storeCode',
      title: t('store'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, record) => `${record.storeCode} - ${record.storeName}` ?? '-',
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            overlay={
              <Menu>
                {edit && (
                  <Menu.Item
                    key="edit"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setIsEditModalOpen(true);
                    }}
                  >
                    {t('goToDetail')}
                  </Menu.Item>
                )}
                {changeCourier && (
                  <Menu.Item
                    key="changeCourier"
                    icon={<SwapOutlined />}
                    onClick={() => {
                      changeOrderCourier(dataItem.zoneId, dataItem.status);
                    }}
                  >
                    {t('changeCourier')}
                  </Menu.Item>
                )}
                {changeStatus && (
                  <Menu.SubMenu
                    key="changeStatus"
                    disabled={[
                      ORDER_STATUSES.CANCELLED,
                      ORDER_STATUSES.DELIVERED,
                      ORDER_STATUSES.NOT_DELIVERED,
                      ORDER_STATUSES.COMPLETED,
                      ORDER_STATUSES.WAITING_FOR_APPROVAL,
                      ORDER_STATUSES.ARRIVED_AT_STORE,
                      ORDER_STATUSES.COLLECTING,
                    ].includes(dataItem.status)}
                    title={
                      <span>
                        <RetweetOutlined /> {t('changeStatus')}
                      </span>
                    }
                  >
                    <>
                      {createMenuItems(
                        dataItem,
                        ORDER_STATUSES.CANCELLED,
                        'CANCELLED',
                      )}
                      {createMenuItems(
                        dataItem,
                        ORDER_STATUSES.DELIVERED,
                        'DELIVERED',
                      )}
                      {createMenuItems(
                        dataItem,
                        ORDER_STATUSES.NOT_DELIVERED,
                        'NOT_DELIVERED',
                      )}
                    </>
                  </Menu.SubMenu>
                )}
                {orderTransfer && (
                  <Menu.Item
                    key="orderTransfer"
                    icon={<RollbackOutlined />}
                    disabled={dataItem.status !== ORDER_STATUSES.NEW_ORDER}
                    onClick={() => {
                      orderTransferFn(dataItem.customerOrderNumber);
                    }}
                  >
                    {t('orderTransfer')}
                  </Menu.Item>
                )}
              </Menu>
            }
            placement="bottomRight"
            trigger={['click']}
            arrow
            onClick={() => setSelectedItemId(dataItem.id)}
          >
            <Button shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
        ),
    },
  ];

  const orderTransferFn = (orderNumber) => {
    setOpenOrderTransferModal(true);
    setOrderNumberForTransfer(orderNumber);
  };

  const changeOrderCourier = (orderZone, orderStatus) => {
    if (!COURIER_CHANGE_STATES.includes(orderStatus)) {
      notification.warn({
        message: t('warning'),
        description: t('statusOfTheOrderIsNotSuitableForChangingTheCourier'),
      });
      return;
    }

    setIsChangeCourierModalOpen(true);

    if (orderZone) {
      let queryString = `filter=user.roles.active==true and user.roles.zone.id==${orderZone} and active==true`;
      setGetCourierLoading(true);
      getCourier(queryString)
        .then((courier) => {
          const { _embedded, page } = courier;
          const { courierResponseDtoList } = _embedded ? _embedded : [];
          setCouriersOfZone(courierResponseDtoList ?? []);
        })
        .finally(() => setGetCourierLoading(false));
    }
  };
  const createMenuItems = (dataItem, statusKey, statusText) => (
    <Menu.Item
      key={statusKey}
      onClick={() => {
        showConfirmForOrderChangeStatus(dataItem.id, statusKey);
      }}
    >
      {t(statusText)}
    </Menu.Item>
  );

  const showConfirmForOrderChangeStatus = (id, status) => {
    const statusTranslate = t(status);
    const confirmationMessage = t('doYouConfirmChangeOrderStatus', {
      statusTranslate,
    });

    const actionMap = {
      [ORDER_STATUSES.CANCELLED]: cancelOrderManually,
      [ORDER_STATUSES.DELIVERED]: deliverOrderManually,
      [ORDER_STATUSES.NOT_DELIVERED]: notDeliverOrderManually,
    };

    const action = actionMap[status];

    confirm({
      title: t('doYouConfirm'),
      icon: <ExclamationCircleOutlined />,
      content: confirmationMessage,
      okText: t('ok'),
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        action(id, status).then((res) => {
          if (res) {
            notificationChangeStatus('success');
            fetchData(initialPageParams, filteringValue);
          }
        });
      },
      onCancel() {},
    });
  };
  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('orderStatusChanged'),
    });
  };
  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      reportType: 'actionType',
      directorateId: 'directorate',
      regionId: 'administrativeRegion',
      storeId: 'store',
    };

    return Object.keys(filteringValue)
      .filter((item) => item !== '_labels')
      .map((item, idx) => {
        if (filteringValue[item]) {
          const rawVal = filteringValue[item];
          const label = filteringValue._labels?.[item];
          const displayVal =
            item === 'reportType' ? t(rawVal) : label || rawVal;
          return (
            <Tag
              key={idx}
              closable
              color="geekblue"
              onClose={(e) => {
                e.preventDefault();
                removeItemFromFilters(item);
              }}
            >
              {`${t(fieldLocalizationVars[item] || item)} : ${displayVal}`}
            </Tag>
          );
        }
        return null;
      });
  };
  const removeItemFromFilters = (destinationItem) => {
    const cascadeMap = {
      directorateId: ['directorateId', 'regionId', 'storeId'],
      regionId: ['regionId', 'storeId'],
      storeId: ['storeId'],
      reportType: ['reportType'],
    };
    const fieldsToClear = cascadeMap[destinationItem] || [destinationItem];
    const clearedValues = fieldsToClear.reduce(
      (acc, f) => ({ ...acc, [f]: undefined }),
      {},
    );
    const clearedLabels = fieldsToClear.reduce(
      (acc, f) => ({ ...acc, [f]: undefined }),
      {},
    );
    const editedFilters = {
      ...filteringValue,
      ...clearedValues,
      _labels: { ...filteringValue._labels, ...clearedLabels },
    };
    setFilteringValue(editedFilters);
    form.resetFields(fieldsToClear);
    fetchData(initialPageParams, editedFilters);
  };

  const renderActionButtons = () => {
    return (
      <Tooltip title={t('download')}>
        <Button
          style={{ marginRight: '3px' }}
          shape="round"
          icon={<DownloadOutlined />}
          disabled={!rows.length}
          onClick={() => console.log('download')}
        ></Button>
      </Tooltip>
    );
  };

  const handleTableChange = (pagination, filters1, sorter) => {
    fetchData(
      {
        pagination,
        ...sorter,
      },
      filteringValue,
    );
  };

  const getTableMessage = () => {
    if (
      !filteringValue.reportType ||
      (!activeStoreId && !filteringValue.storeId)
    ) {
      return <Alert message={t('ordersPendingActionMessage')} type="warning" />;
    }
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{/*renderActionButtons()*/}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) =>
              `${record.customerOrderNumber}_${record?.createdDate || ''}`
            }
            locale={{
              emptyText: getTableMessage(),
            }}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <TableFilter
            setIsFiltersOpen={setIsFiltersOpen}
            form={form}
            filterFormValues={filteringValue}
            handlerFilter={(filterValue) =>
              fetchData(initialPageParams, filterValue)
            }
          />
        )}
        {isEditModalOpen && (
          <Edit
            orderId={selectedItemId}
            onCloseHandler={() => setIsEditModalOpen(false)}
          />
        )}
        {isChangeCourierModalOpen && (
          <ChangeOrderCourier
            orderId={selectedItemId}
            couriers={couriersOfZone}
            onCloseHandler={() => setIsChangeCourierModalOpen(false)}
            onSuccessCallback={() => {
              setIsChangeCourierModalOpen(false);
              fetchData(initialPageParams, filteringValue);
            }}
            getCourierLoading={getCourierLoading}
          />
        )}
        {openOrderTransferModal && (
          <OrderTransfer
            orderNumber={orderNumberForTransfer}
            onSuccessCalback={() => {
              setOpenOrderTransferModal(false);
              fetchData(initialPageParams, filteringValue);
            }}
            onCloseHandler={() => {
              setOpenOrderTransferModal(false);
            }}
          />
        )}
      </Suspense>
    </div>
  );
};

export default OrdersPendingAction;
